// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — FlowCanvas (M3: V·IANet Flow)
// Main React Flow canvas with custom node types and DnD support
// Capa 1C: A3 fixed canvas, dots grid, snap-to-grid, NODE_TYPES
// Capa 2A: Swimlanes background layer
// Features: Context menu, inline editing, edge selection, mouse coords
// ═══════════════════════════════════════════════════════════════

import { useCallback, useRef, useMemo, useState } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Panel,
  addEdge,
  useReactFlow,
  useViewport,
  type OnConnect,
  type OnNodesChange,
  type OnEdgesChange,
  type NodeTypes,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type {
  FlowNode,
  FlowEdge,
  FlowNodeData,
  TipoNodoFlujograma,
  Swimlane,
  FlowPhase,
} from '../types/flowchart.types';
import type { FlowRole } from '../shared/SwimlaneRenderer';

import { FLOW_TOKENS as T } from '../shared/flowTokens';
import SwimlaneRenderer from '../shared/SwimlaneRenderer';

// ─── Node + Edge imports ─────────────────────────────────────────────────────

import { NODE_TYPES } from './nodes';
import { NODE_TYPE_LABELS } from './utils/flowSchema';
import { OrthogonalEdge } from './edges/OrthogonalEdge';

const EDGE_TYPES = { orthogonal: OrthogonalEdge };

import FlowMinimap from '../shared/FlowMinimap';
import ContextMenu, { type ContextMenuAction, type ContextMenuType } from './ContextMenu';

// ─── A3 Canvas Constants ────────────────────────────────────────────────────

const A3_W = T.canvas.a3Width;
const A3_H = T.canvas.a3Height;

// ─── Node Type Registry ──────────────────────────────────────────────────────

const NODE_TYPE_MAP: NodeTypes = NODE_TYPES;

// ─── Default Node Data ───────────────────────────────────────────────────────

const DEFAULT_LABELS: Record<TipoNodoFlujograma, string> = NODE_TYPE_LABELS;

// ─── Props ───────────────────────────────────────────────────────────────────

interface FlowCanvasProps {
  nodes: FlowNode[];
  edges: FlowEdge[];
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  setNodes: React.Dispatch<React.SetStateAction<FlowNode[]>>;
  setEdges: React.Dispatch<React.SetStateAction<FlowEdge[]>>;
  onNodeSelect?: (node: FlowNode | null, event?: React.MouseEvent) => void;
  onEdgeSelect?: (edge: FlowEdge | null) => void;
  showGrid: boolean;
  showMinimap: boolean;
  lanes?: Swimlane[];
  phases?: FlowPhase[];
  roles?: FlowRole[];
  onMousePosition?: (pos: { x: number; y: number } | null) => void;
  onContextAction?: (
    action: ContextMenuAction,
    targetId?: string,
    targetType?: ContextMenuType,
  ) => void;
  onNodeDoubleClick?: (node: FlowNode) => void;
  onEdgeDoubleClick?: (edge: FlowEdge) => void;
  onRenamePhase?: (id: string, name: string) => void;
  onRemovePhase?: (id: string) => void;
  readOnly?: boolean;
  onNodeClick?: (node: FlowNode, event: React.MouseEvent) => void;
  onFitToCanvas?: () => void;
}

// ─── A3 Sheet Background Component ──────────────────────────────────────────

function A3Sheet() {
  const { x, y, zoom } = useViewport();

  const sheetStyle: React.CSSProperties = {
    position: 'absolute',
    left: x,
    top: y,
    width: A3_W * zoom,
    height: A3_H * zoom,
    border: `1px solid ${T.canvas.sheetBorder}`,
    borderRadius: 2,
    backgroundColor: '#FFFFFF',
    pointerEvents: 'none',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    zIndex: -1,
  };

  return <div style={sheetStyle} />;
}

// ─── Swimlane Viewport Wrapper (renders inside ReactFlow, transforms with zoom/pan) ──

function SwimlaneViewportLayer({
  lanes,
  phases,
  roles,
  onRenamePhase,
  onRemovePhase,
  readOnly,
}: {
  lanes: { id: string; name: string; y: number; height: number }[];
  phases?: FlowPhase[];
  roles?: FlowRole[];
  onRenamePhase?: (id: string, name: string) => void;
  onRemovePhase?: (id: string) => void;
  readOnly?: boolean;
}) {
  const { x, y, zoom } = useViewport();

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: A3_W * zoom,
        height: A3_H * zoom,
        transformOrigin: '0 0',
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{ transform: `scale(${zoom})`, transformOrigin: '0 0', width: A3_W, height: A3_H }}
      >
        <SwimlaneRenderer
          lanes={lanes}
          phases={phases}
          roles={roles}
          onRenamePhase={onRenamePhase}
          onRemovePhase={onRemovePhase}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}

// ─── A3 Label Overlay ───────────────────────────────────────────────────────

function A3Label() {
  const { x, y, zoom } = useViewport();

  return (
    <div
      style={{
        position: 'absolute',
        left: x + (A3_W * zoom) / 2,
        top: y - 18,
        transform: 'translateX(-50%)',
        fontSize: '9px',
        fontWeight: 600,
        fontFamily: T.font.family,
        color: T.color.textTertiary,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: 5,
      }}
    >
      1500 &times; 880
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function FlowCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  setNodes,
  setEdges,
  onNodeSelect,
  onEdgeSelect,
  showGrid,
  showMinimap,
  lanes,
  phases,
  roles,
  onMousePosition,
  onContextAction,
  onNodeDoubleClick,
  onEdgeDoubleClick,
  onRenamePhase,
  onRemovePhase,
  readOnly,
  onNodeClick: onNodeClickProp,
  onFitToCanvas,
}: FlowCanvasProps) {
  const { screenToFlowPosition, zoomIn, zoomOut } = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    type: ContextMenuType;
    targetId?: string;
  } | null>(null);

  // Memoize nodeTypes to prevent re-renders
  const nodeTypes = useMemo(() => NODE_TYPE_MAP, []);

  // Map Swimlane[] to SwimlaneRenderLane[]
  const swimlaneRenderLanes = useMemo(() => {
    if (!lanes || lanes.length === 0) return [];
    return lanes.map((l) => ({
      id: l.id,
      name: l.puestoNombre,
      y: l.y,
      height: l.height,
    }));
  }, [lanes]);

  // ─── Connection Handler ───
  const onConnect: OnConnect = useCallback(
    (connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            type: 'orthogonal',
            data: { arrowType: 'arrow', lineStyle: 'solid' },
          },
          eds,
        ),
      );
    },
    [setEdges],
  );

  // ─── Drop Handler (from ShapeLibrary) ───
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow') as TipoNodoFlujograma;
      if (!type) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Clamp position inside A3
      position.x = Math.max(0, Math.min(position.x, A3_W - T.node.width));
      position.y = Math.max(0, Math.min(position.y, A3_H - T.node.height));

      const isDecision = type === 'DECISION';
      const isRef = type === 'PROCESO_REF';
      // M5: PROCESO_REF usa refRadius*2 (80px), DECISION usa decisionSize
      const nodeW = isRef ? T.node.refRadius * 2 : isDecision ? T.node.decisionSize : T.node.width;
      const nodeH = isRef ? T.node.refRadius * 2 : isDecision ? T.node.decisionSize : T.node.height;
      const newNode: FlowNode = {
        id: `node_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        type,
        position,
        data: {
          label: DEFAULT_LABELS[type] ?? type,
          nodeType: type,
        } as FlowNodeData,
        style: {
          width: nodeW,
          height: nodeH,
        },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [screenToFlowPosition, setNodes],
  );

  // ─── Node Click ───
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: FlowNode) => {
      onNodeSelect?.(node, event);
      onEdgeSelect?.(null);
      onNodeClickProp?.(node, event);
    },
    [onNodeSelect, onEdgeSelect, onNodeClickProp],
  );

  // ─── Edge Click ───
  const handleEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: FlowEdge) => {
      onEdgeSelect?.(edge);
      onNodeSelect?.(null);
    },
    [onEdgeSelect, onNodeSelect],
  );

  // ─── Pane Click (deselect) ───
  const onPaneClick = useCallback(() => {
    onNodeSelect?.(null);
    onEdgeSelect?.(null);
    setContextMenu(null);
  }, [onNodeSelect, onEdgeSelect]);

  // ─── Double Click Handlers ───
  const handleNodeDoubleClick = useCallback(
    (_event: React.MouseEvent, node: FlowNode) => {
      onNodeDoubleClick?.(node);
    },
    [onNodeDoubleClick],
  );

  const handleEdgeDoubleClick = useCallback(
    (_event: React.MouseEvent, edge: FlowEdge) => {
      onEdgeDoubleClick?.(edge);
    },
    [onEdgeDoubleClick],
  );

  // ─── Context Menu Handlers ───
  const handleNodeContextMenu = useCallback((event: React.MouseEvent, node: FlowNode) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, type: 'node', targetId: node.id });
  }, []);

  const handleEdgeContextMenu = useCallback((event: React.MouseEvent, edge: FlowEdge) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, type: 'edge', targetId: edge.id });
  }, []);

  const handlePaneContextMenu = useCallback((event: React.MouseEvent | MouseEvent) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, type: 'canvas' });
  }, []);

  const handleContextAction = useCallback(
    (action: ContextMenuAction) => {
      onContextAction?.(action, contextMenu?.targetId, contextMenu?.type);
      setContextMenu(null);
    },
    [onContextAction, contextMenu],
  );

  // ─── Mouse Move (for coordinates bar) ───
  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!onMousePosition) return;
      const pos = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      onMousePosition({ x: Math.round(pos.x), y: Math.round(pos.y) });
    },
    [screenToFlowPosition, onMousePosition],
  );

  const handleMouseLeave = useCallback(() => {
    onMousePosition?.(null);
  }, [onMousePosition]);

  // ─── Keyboard: Delete selected ───
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        setNodes((nds) => nds.filter((n) => !n.selected));
        setEdges((eds) => eds.filter((e) => !e.selected));
      }
    },
    [setNodes, setEdges],
  );

  return (
    <div
      ref={reactFlowWrapper}
      className="flex-1 h-full"
      style={{ position: 'relative', backgroundColor: T.canvas.outsideBg }}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={handleEdgeClick}
        onPaneClick={onPaneClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeDoubleClick={handleEdgeDoubleClick}
        onNodeContextMenu={handleNodeContextMenu}
        onEdgeContextMenu={handleEdgeContextMenu}
        onPaneContextMenu={handlePaneContextMenu}
        onDragOver={readOnly ? undefined : onDragOver}
        onDrop={readOnly ? undefined : onDrop}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        nodeTypes={nodeTypes}
        edgeTypes={EDGE_TYPES}
        snapToGrid
        snapGrid={[T.canvas.snapGrid, T.canvas.snapGrid]}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        selectionOnDrag={!readOnly}
        nodeExtent={[
          [0, 0],
          [A3_W - T.node.width, A3_H - T.node.height],
        ]}
        fitView={false}
        minZoom={0.1}
        maxZoom={3}
        deleteKeyCode={readOnly ? null : ['Delete', 'Backspace']}
        multiSelectionKeyCode="Control"
        selectionKeyCode="Control"
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: 'orthogonal',
          animated: false,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: T.color.connector,
            width: 14,
            height: 14,
          },
        }}
        style={{ background: T.canvas.outsideBg }}
      >
        {/* A3 sheet white rectangle */}
        <Panel
          position="top-left"
          style={{
            margin: 0,
            padding: 0,
            pointerEvents: 'none',
            position: 'absolute',
            inset: 0,
            zIndex: -1,
          }}
        >
          <A3Sheet />
          <A3Label />
        </Panel>

        {/* Swimlanes + Phases — rendered inside ReactFlow viewport so they zoom/pan */}
        {(swimlaneRenderLanes.length > 0 || (phases && phases.length > 0)) && (
          <Panel
            position="top-left"
            style={{
              margin: 0,
              padding: 0,
              pointerEvents: 'none',
              position: 'absolute',
              inset: 0,
              zIndex: 0,
            }}
          >
            <SwimlaneViewportLayer
              lanes={swimlaneRenderLanes}
              phases={phases}
              roles={roles}
              onRenamePhase={onRenamePhase}
              onRemovePhase={onRemovePhase}
              readOnly={readOnly}
            />
          </Panel>
        )}

        {showGrid && (
          <Background
            variant={BackgroundVariant.Dots}
            gap={T.canvas.snapGrid}
            size={1.2}
            color={`${T.color.border}88`}
          />
        )}
        {/* Custom zoom controls with fitToCanvas */}
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {[
            { label: '+', title: 'Acercar', action: () => zoomIn() },
            { label: '\u2212', title: 'Alejar', action: () => zoomOut() },
            { label: '\u229F', title: 'Ajustar A3', action: () => onFitToCanvas?.() },
          ].map((btn) => (
            <button
              key={btn.title}
              onClick={btn.action}
              title={btn.title}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 26,
                height: 26,
                borderRadius: 4,
                border: '1px solid #D9D9D9',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 14,
                color: '#595959',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                fontFamily: 'inherit',
              }}
            >
              {btn.label}
            </button>
          ))}
        </div>
        <FlowMinimap visible={showMinimap} />

        {/* Markers handled by MarkerType objects in OrthogonalEdge — no custom SVG defs needed */}
      </ReactFlow>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          type={contextMenu.type}
          onAction={handleContextAction}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
